import React, { useState } from "react";
import { AppShell } from "../components/duvi/AppShell";
import { Card } from "../components/duvi/Card";
import { Button } from "../components/duvi/Button";
import { Badge } from "../components/duvi/Badge";
import { AlertTriangle, Check, StickyNote } from "lucide-react";
import { useParams } from "react-router";
import {
  tickets as ticketsApi,
  type TicketDetail as TicketDetailType,
  type TicketMessage,
} from "../api/client";
import { useQuery } from "../hooks/useApi";
import { testIds } from "@duvi-agency/ui";

export function SupportTicketDetail() {
  const { id } = useParams();
  const [replyText, setReplyText] = useState("");
  const [isInternalNote, setIsInternalNote] = useState(false);
  const [isSending, setIsSending] = useState(false);
  const [isResolving, setIsResolving] = useState(false);
  const [isClosing, setIsClosing] = useState(false);

  const {
    data: ticketDetail,
    isLoading,
    refetch,
  } = useQuery(() => ticketsApi.get(id!), [id]);

  const ticket = ticketDetail?.ticket;
  const messages = ticketDetail?.messages ?? [];

  const [status, setStatus] = useState("");
  const [priority, setPriority] = useState("");

  // Sync local state when ticket loads
  React.useEffect(() => {
    if (ticket) {
      setStatus(ticket.status);
      setPriority(ticket.priority);
    }
  }, [ticket]);

  const handleSendReply = async () => {
    if (!replyText.trim() || !id) return;
    setIsSending(true);
    try {
      await ticketsApi.addMessage(id, {
        content: replyText,
        is_internal: isInternalNote,
      });
      setReplyText("");
      setIsInternalNote(false);
      refetch();
    } catch {
      // error handling deferred
    } finally {
      setIsSending(false);
    }
  };

  const handleResolve = async () => {
    if (!id) return;
    setIsResolving(true);
    try {
      await ticketsApi.resolve(id);
      refetch();
    } catch {
      // error handling deferred
    } finally {
      setIsResolving(false);
    }
  };

  const handleClose = async () => {
    if (!id) return;
    setIsClosing(true);
    try {
      await ticketsApi.close(id);
      refetch();
    } catch {
      // error handling deferred
    } finally {
      setIsClosing(false);
    }
  };

  const relatedTickets = [
    { id: "t3a7b2c8", subject: "Dashboard loading slowly" },
    { id: "t2c9d1e4", subject: "Unable to view customer list" },
    { id: "t1b8e3f5", subject: "API timeout errors" },
  ];

  const getPriorityColor = (p: string) => {
    switch (p) {
      case "urgent":
        return "#EF4444";
      case "high":
        return "#F59E0B";
      case "medium":
        return "#3B82F6";
      case "low":
        return "#64748B";
      default:
        return "#64748B";
    }
  };

  const getStatusBadgeVariant = (s: string) => {
    switch (s) {
      case "open":
        return "default";
      case "in-progress":
        return "warning";
      case "awaiting":
        return "default";
      case "resolved":
        return "success";
      case "closed":
        return "secondary";
      default:
        return "default";
    }
  };

  return (
    <AppShell
      breadcrumbs={[
        "Support",
        "Tickets",
        ticket ? `#${ticket.ticket_id}` : `#${id}`,
      ]}
      activeMenuItem="Support"
      notificationCount={3}
    >
      {/*
       * Page identity anchor — always rendered inside AppShell so E2E can
       * assert we've landed on this page regardless of data-load state.
       * (Story 29 pattern: never gate the landmark behind a data conditional.)
       */}
      <div data-testid={testIds.ticketDetailPage}>
        {isLoading || !ticket ? (
          <div className="flex items-center justify-center py-16">
            <span
              className="text-duvi-text-secondary"
              style={{ fontSize: "var(--text-sm)" }}
            >
              Loading ticket...
            </span>
          </div>
        ) : (
          /* Two Column Layout */
          <div className="grid grid-cols-[70%_30%] gap-6">
            {/* Left Column */}
            <div className="space-y-6">
              {/* Subject and Badges */}
              <div>
                <h1
                  className="text-duvi-text-primary mb-3"
                  style={{
                    fontSize: "var(--text-xl)",
                    fontWeight: "var(--font-weight-bold)",
                    lineHeight: "var(--line-height-heading)",
                  }}
                >
                  {ticket.subject}
                </h1>
                <div className="flex items-center gap-2">
                  <Badge
                    style={{
                      backgroundColor: getPriorityColor(ticket.priority) + "20",
                      color: getPriorityColor(ticket.priority),
                      borderColor: getPriorityColor(ticket.priority),
                    }}
                  >
                    {ticket.priority.charAt(0).toUpperCase() +
                      ticket.priority.slice(1)}{" "}
                    Priority
                  </Badge>
                  <Badge variant={getStatusBadgeVariant(ticket.status)}>
                    {ticket.status.charAt(0).toUpperCase() +
                      ticket.status.slice(1)}
                  </Badge>
                  <Badge variant="secondary">{ticket.category}</Badge>
                </div>
              </div>

              {/* Submitter and SLA */}
              <div className="space-y-3">
                <div
                  className="text-duvi-text-secondary"
                  style={{ fontSize: "var(--text-sm)" }}
                >
                  {ticket.submitter_id} · {ticket.submitter_role}
                </div>
              </div>

              {/* Message Thread */}
              <div className="space-y-4">
                {messages.map((message) => (
                  <React.Fragment key={message.msg_id}>
                    <Card
                      className={`p-4 ${
                        message.author_role === "admin" ||
                        message.author_role === "Support Admin"
                          ? "border-l-[3px] border-l-duvi-accent"
                          : ""
                      } ${message.is_internal ? "bg-duvi-warning/[0.06]" : ""}`}
                    >
                      <div className="flex items-start gap-3">
                        <div
                          className="w-8 h-8 rounded-full bg-duvi-accent flex items-center justify-center text-white flex-shrink-0"
                          style={{
                            fontSize: "var(--text-xs)",
                            fontWeight: "var(--font-weight-semibold)",
                          }}
                        >
                          {message.author_id.slice(0, 2).toUpperCase()}
                        </div>
                        <div className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-2">
                            <span
                              className="text-duvi-text-primary"
                              style={{
                                fontSize: "var(--text-sm)",
                                fontWeight: "var(--font-weight-medium)",
                              }}
                            >
                              {message.author_id}
                            </span>
                            <Badge variant="secondary" className="text-[10px]">
                              {message.author_role}
                            </Badge>
                            {message.is_internal && (
                              <Badge variant="warning" className="text-[10px]">
                                Internal note
                              </Badge>
                            )}
                            <span
                              className="text-duvi-text-secondary ml-auto"
                              style={{ fontSize: "var(--text-xs)" }}
                            >
                              {message.created_at}
                            </span>
                          </div>
                          <p
                            className="text-duvi-text-secondary"
                            style={{ fontSize: "var(--text-sm)" }}
                          >
                            {message.content}
                          </p>
                        </div>
                      </div>
                    </Card>
                  </React.Fragment>
                ))}
              </div>

              {/* Reply Input */}
              <Card className="p-4">
                <div className="space-y-3">
                  <textarea
                    data-testid={testIds.ticketReplyInput}
                    value={replyText}
                    onChange={(e) => setReplyText(e.target.value)}
                    placeholder="Type your reply..."
                    className="w-full px-3 py-2 bg-duvi-bg border border-duvi-border rounded-[6px] text-duvi-text-primary placeholder:text-duvi-text-muted focus:outline-none focus:border-duvi-accent transition-colors resize-none"
                    style={{
                      fontSize: "var(--text-sm)",
                      minHeight: "120px",
                    }}
                  />
                  <div className="flex items-center justify-between">
                    <button
                      data-testid={testIds.ticketInternalNoteToggle}
                      onClick={() => setIsInternalNote(!isInternalNote)}
                      className={`flex items-center gap-2 px-3 py-2 rounded-[6px] transition-colors ${
                        isInternalNote
                          ? "bg-duvi-warning/10 text-duvi-warning"
                          : "text-duvi-text-secondary hover:text-duvi-text-primary hover:bg-duvi-elevated"
                      }`}
                      style={{ fontSize: "var(--text-sm)" }}
                    >
                      <StickyNote className="w-4 h-4" />
                      Internal note
                    </button>
                    <Button
                      data-testid={testIds.ticketReplySubmit}
                      variant="primary"
                      onClick={handleSendReply}
                      disabled={isSending || !replyText.trim()}
                    >
                      {isSending ? "Sending..." : "Send reply"}
                    </Button>
                  </div>
                </div>
              </Card>
            </div>

            {/* Right Column */}
            <div className="space-y-6">
              {/* Actions Card */}
              <Card className="p-5">
                <h3
                  className="text-duvi-text-primary mb-4"
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                  }}
                >
                  Actions
                </h3>
                <div className="space-y-3">
                  {/* Status Dropdown */}
                  <div>
                    <label
                      className="block text-duvi-text-secondary mb-2"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      Status
                    </label>
                    <select
                      value={status}
                      onChange={(e) => setStatus(e.target.value)}
                      className="w-full px-3 py-2 bg-duvi-bg border border-duvi-border rounded-[6px] text-duvi-text-primary focus:outline-none focus:border-duvi-accent transition-colors"
                      style={{ fontSize: "var(--text-sm)" }}
                    >
                      <option value="open">Open</option>
                      <option value="in-progress">In Progress</option>
                      <option value="awaiting">Awaiting</option>
                      <option value="resolved">Resolved</option>
                      <option value="closed">Closed</option>
                    </select>
                  </div>

                  {/* Priority Dropdown */}
                  <div>
                    <label
                      className="block text-duvi-text-secondary mb-2"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      Priority
                    </label>
                    <select
                      value={priority}
                      onChange={(e) => setPriority(e.target.value)}
                      className="w-full px-3 py-2 bg-duvi-bg border border-duvi-border rounded-[6px] text-duvi-text-primary focus:outline-none focus:border-duvi-accent transition-colors"
                      style={{ fontSize: "var(--text-sm)" }}
                    >
                      <option value="urgent">Urgent</option>
                      <option value="high">High</option>
                      <option value="medium">Medium</option>
                      <option value="low">Low</option>
                    </select>
                  </div>

                  <Button
                    data-testid={testIds.ticketResolve}
                    variant="secondary"
                    className="w-full"
                    onClick={handleResolve}
                    disabled={isResolving}
                  >
                    {isResolving ? "Resolving..." : "Resolve"}
                  </Button>

                  <Button
                    data-testid={testIds.ticketClose}
                    variant="destructive-outline"
                    className="w-full"
                    onClick={handleClose}
                    disabled={isClosing}
                  >
                    {isClosing ? "Closing..." : "Close ticket"}
                  </Button>
                </div>
              </Card>

              {/* Metadata Card */}
              <Card className="p-5">
                <h3
                  className="text-duvi-text-primary mb-4"
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                  }}
                >
                  Metadata
                </h3>
                <div className="space-y-3">
                  <div>
                    <div
                      className="text-duvi-text-secondary mb-1"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      Created
                    </div>
                    <div
                      className="text-duvi-text-primary"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      {ticket.created_at}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-duvi-text-secondary mb-1"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      Last updated
                    </div>
                    <div
                      className="text-duvi-text-primary"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      {ticket.updated_at}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-duvi-text-secondary mb-1"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      Assigned level
                    </div>
                    <div
                      className="text-duvi-text-primary"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      {ticket.assigned_level}
                    </div>
                  </div>
                  <div>
                    <div
                      className="text-duvi-text-secondary mb-1"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      Category
                    </div>
                    <div
                      className="text-duvi-text-primary"
                      style={{ fontSize: "var(--text-xs)" }}
                    >
                      {ticket.category}
                    </div>
                  </div>
                </div>
              </Card>

              {/* Related Tickets Card */}
              <Card className="p-5">
                <h3
                  className="text-duvi-text-primary mb-4"
                  style={{
                    fontSize: "var(--text-sm)",
                    fontWeight: "var(--font-weight-semibold)",
                  }}
                >
                  Related tickets
                </h3>
                <div className="space-y-2">
                  {relatedTickets.map((related) => (
                    <a
                      key={related.id}
                      href={`/support/tickets/${related.id}`}
                      className="block hover:bg-duvi-elevated p-2 rounded-[6px] transition-colors"
                    >
                      <div
                        className="text-duvi-accent font-mono mb-1"
                        style={{
                          fontSize: "var(--text-xs)",
                          fontFamily: "monospace",
                        }}
                      >
                        {related.id}
                      </div>
                      <div
                        className="text-duvi-text-secondary truncate"
                        style={{ fontSize: "var(--text-xs)" }}
                      >
                        {related.subject}
                      </div>
                    </a>
                  ))}
                </div>
              </Card>
            </div>
          </div>
        )}
      </div>
    </AppShell>
  );
}
